import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import {
  History,
  Download,
  ArrowLeft,
  ArrowRightLeft,
  LogOut,
  QrCode
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

function SafetyQRHistory({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const hasPermission = (perm) => permissions.includes(perm);

  useEffect(() => {
    fetchPermissions();
    fetchBatches();
  }, []);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      setPermissions(res.data.effective_permissions || []);
    } catch (error) {
      console.error("Failed to load permissions:", error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API}/qr/batches`, getAuthHeaders());
      setBatches(response.data);
    } catch (error) {
      console.error("Failed to load batches:", error);
      toast.error("Failed to load QR history");
    } finally {
      setLoading(false);
    }
  };

  // Download ZIP
  const downloadZip = async (batch) => {
    try {
      const response = await axios.get(`${API}/qr/batches/${batch.id}/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${batch.po_number}_Batch${batch.batch_number}_${batch.qr_date}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("ZIP downloaded successfully");
    } catch (error) {
      toast.error("Failed to download ZIP");
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // If YYYYMMDD format
    if (dateStr.length === 8 && !dateStr.includes('-')) {
      return `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
    }
    return dateStr;
  };

  return (
    <div data-testid="safety-qr-history">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/safety")}
              variant="ghost"
              className="text-white hover:bg-white/10 -ml-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <History className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">QR Generation History</h1>
              <p className="text-emerald-100 text-sm">View and re-download past QR batches</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission("module.project") && (
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Project Module
              </Button>
            )}
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : batches.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <QrCode className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No QR batches generated yet</p>
              <Button 
                variant="link" 
                onClick={() => navigate("/safety/qr")}
                className="text-emerald-600"
              >
                Generate your first QR codes
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">PO Number</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Batch</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">QR Date</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">QR Codes</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Created By</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Created At</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.id} className="border-b hover:bg-gray-50" data-testid={`batch-row-${batch.id}`}>
                    <td className="px-4 py-3 font-mono font-medium">{batch.po_number}</td>
                    <td className="px-4 py-3">{batch.batch_number}</td>
                    <td className="px-4 py-3">{formatDate(batch.qr_date)}</td>
                    <td className="px-4 py-3 text-center">{batch.total_codes}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{batch.created_by}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {new Date(batch.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => downloadZip(batch)}
                        data-testid={`download-batch-${batch.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default SafetyQRHistory;
